/* Native scrolling for rails; button navigation for transform-based banners. */
(() => {
  'use strict';
  if (window.sunnySwipeReady) return;
  window.sunnySwipeReady = true;
  const initialized = new WeakSet();
  const wheelStates = new WeakMap();
  const blockedClicks = new WeakMap();
  const controls = 'button, input, select, textarea, [contenteditable="true"], [role="button"]';
  const columns = (value, fallback) => Math.max(1, Math.min(8, Number(value) || fallback));

  function init(root) {
    window.sunnyInitHeroes?.(root);
    const rails = [...root.querySelectorAll('[data-sunny-rail]')];
    if (root.matches?.('[data-sunny-rail]')) rails.push(root);
    rails.forEach(rail => {
      if (initialized.has(rail)) return;
      initialized.add(rail);
      rail.style.setProperty('--sunny-columns', columns(rail.dataset.sunnyColumns, 4));
      rail.style.setProperty('--sunny-mobile-columns', columns(rail.dataset.sunnyMobileColumns, 2));
      const gap = parseFloat(getComputedStyle(rail).columnGap);
      // Inline gap variable is intentionally optional: the shared CSS supplies 16px.
      if (Number.isFinite(gap)) rail.style.setProperty('--sunny-rail-gap', `${gap}px`);
      if (!rail.hasAttribute('tabindex')) rail.tabIndex = 0;
      if (!rail.hasAttribute('aria-label') && !rail.hasAttribute('aria-labelledby')) {
        const section = rail.closest('.shopify-section');
        const heading = section?.querySelector('h1, h2, h3');
        if (heading) rail.setAttribute('aria-label', heading.textContent.trim());
      }
      const slider = rail.closest('slider-component');
      if (slider?.nextButton && typeof slider.resetPages === 'function') slider.resetPages();
    });
  }

  function targetElement(event) {
    return event.target instanceof Element ? event.target : event.target?.parentElement;
  }
  function bannerFor(element) {
    return element?.closest('[id^="SunnyHero-"], slideshow-component');
  }
  function arrow(banner, forward) {
    return banner.querySelector(banner.matches('slideshow-component')
      ? `button[name="${forward ? 'next' : 'previous'}"]`
      : `.sunny-hero__arrow--${forward ? 'next' : 'prev'}`);
  }
  function canStep(banner, forward) {
    if (!banner.matches('slideshow-component')) return banner.querySelectorAll('.sunny-hero__slide').length > 1;
    const button = arrow(banner, forward);
    return Boolean(button && !button.disabled);
  }
  function stepBanner(banner, forward) {
    if (!canStep(banner, forward)) return false;
    if (!banner.matches('slideshow-component')) {
      banner.dispatchEvent(new CustomEvent('sunny:step', { detail: { forward } }));
      return true;
    }
    const button = arrow(banner, forward);
    if (!button || button.disabled) return false;
    button.click();
    return true;
  }

  document.addEventListener('wheel', event => {
    const element = targetElement(event);
    if (!element || element.closest(controls) || event.ctrlKey || event.metaKey || !event.cancelable) return;
    const shifted = event.shiftKey && event.deltaX === 0;
    let dx = shifted ? event.deltaY : event.deltaX;
    const dy = shifted ? 0 : event.deltaY;
    if (!dx || Math.abs(dx) <= Math.abs(dy)) return;
    if (event.deltaMode === 1) dx *= 16;
    if (event.deltaMode === 2) dx *= window.innerWidth;
    const banner = bannerFor(element);
    if (banner) {
      if (!canStep(banner, dx > 0)) return;
      event.preventDefault();
      const now = performance.now();
      let state = wheelStates.get(banner);
      if (!state || now - state.time > 180) state = { sum: 0, stepped: false, time: now };
      state.time = now;
      state.sum += dx;
      if (!state.stepped && Math.abs(state.sum) >= 35) {
        stepBanner(banner, state.sum > 0);
        state.stepped = true;
      }
      wheelStates.set(banner, state);
      return;
    }
    // Horizontal trackpad gestures use browser scrolling. Normalize Shift+wheel
    // only when the browser supplies vertical deltas for that gesture.
    const rail = element.closest('[data-sunny-rail], .slider');
    if (shifted && rail && rail.scrollWidth > rail.clientWidth + 1) {
      event.preventDefault();
      rail.scrollBy({ left: dx, behavior: 'instant' });
    }
  }, { passive: false });

  let touch = null;
  document.addEventListener('touchstart', event => {
    touch = null;
    const element = targetElement(event);
    const hero = element?.closest('[id^="SunnyHero-"]');
    if (!hero || event.touches.length !== 1 || element.closest(controls)) return;
    const point = event.touches[0];
    touch = { hero, id: point.identifier, x: point.clientX, y: point.clientY, axis: null };
  }, { passive: true });

  document.addEventListener('touchmove', event => {
    if (!touch) return;
    if (event.touches.length !== 1) { touch = null; return; }
    const point = event.touches[0];
    if (point.identifier !== touch.id) return;
    const dx = Math.abs(point.clientX - touch.x);
    const dy = Math.abs(point.clientY - touch.y);
    if (!touch.axis && Math.max(dx, dy) >= 8) touch.axis = dx > dy * 1.2 ? 'x' : 'y';
    if (touch.axis === 'x' && event.cancelable) event.preventDefault();
  }, { passive: false });

  document.addEventListener('touchend', event => {
    if (!touch) return;
    const gesture = touch;
    touch = null;
    const point = Array.from(event.changedTouches).find(item => item.identifier === gesture.id);
    if (!point || event.touches.length || gesture.axis === 'y') return;
    const dx = gesture.x - point.clientX;
    const dy = gesture.y - point.clientY;
    if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy) * 1.2) {
      if (stepBanner(gesture.hero, dx > 0)) blockedClicks.set(gesture.hero, performance.now() + 500);
    }
  }, { passive: true });
  document.addEventListener('touchcancel', () => { touch = null; }, { passive: true });
  document.addEventListener('click', event => {
    const hero = targetElement(event)?.closest('[id^="SunnyHero-"]');
    if (hero && event.isTrusted && performance.now() < (blockedClicks.get(hero) || 0)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, true);

  init(document);
  // Includes Theme Editor replacement and asynchronously loaded recommendations.
  const observer = new MutationObserver(records => {
    records.forEach(record => {
      record.removedNodes.forEach(node => {
        if (node instanceof Element && !node.isConnected) window.sunnyDisposeHeroes?.(node);
      });
      record.addedNodes.forEach(node => {
        if (node instanceof Element) init(node);
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
